"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { useEmoji } from "@/contexts/EmojiContext";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import type { ApiAddReactionToDropRequest } from "@/generated/models/ApiAddReactionToDropRequest";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropContextProfileContext } from "@/generated/models/ApiDropContextProfileContext";
import type { ApiDropReaction } from "@/generated/models/ApiDropReaction";
import { formatLargeNumber } from "@/helpers/Helpers";
import { recordReaction } from "@/helpers/reactions/reactionHistory";
import { buildTooltipId } from "@/helpers/tooltip.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import useLongPressInteraction from "@/hooks/useLongPressInteraction";
import { commonApiDelete, commonApiPost } from "@/services/api/common-api";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import { cloneReactionEntries, findReactionIndex, removeUserFromReactions, toProfileMin } from "./reaction-utils";
import styles from "./WaveDropReactions.module.scss";
import WaveDropReactionsDetailDialog from "./WaveDropReactionsDetailDialog";
interface WaveDropReactionsProps {
  readonly drop: ApiDrop;
}
const WaveDropReactions: React.FC<WaveDropReactionsProps> = t0 => {
  const $ = _c(12);
  const {
    drop
  } = t0;
  const [dialogReaction, setDialogReaction] = useState(null);
  const isTouchDevice = useIsTouchDevice();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = reactionKey => {
      setDialogReaction(reactionKey);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleOpenDialog = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setDialogReaction(null);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleCloseDialog = t2;
  let t3;
  if ($[2] !== drop || $[3] !== isTouchDevice) {
    t3 = drop.reactions.map(reaction => <WaveDropReaction key={`${reaction.reaction}-${reaction.profiles.length}`} drop={drop} reaction={reaction} onOpenDetailDialog={handleOpenDialog} isTouchDevice={isTouchDevice} />);
    $[2] = drop;
    $[3] = isTouchDevice;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = dialogReaction !== null;
  const t5 = dialogReaction ?? undefined;
  let t6;
  if ($[5] !== drop.reactions || $[6] !== t4 || $[7] !== t5) {
    t6 = <WaveDropReactionsDetailDialog isOpen={t4} onClose={handleCloseDialog} reactions={drop.reactions} initialReaction={t5} />;
    $[5] = drop.reactions;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t3 || $[10] !== t6) {
    t7 = <>{t3}{t6}</>;
    $[9] = t3;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
};
function WaveDropReaction(t0) {
  const $ = _c(109);
  const {
    drop,
    reaction,
    onOpenDetailDialog,
    isTouchDevice
  } = t0;
  const {
    setToast,
    connectedProfile
  } = useAuth();
  const {
    emojiMap,
    findNativeEmoji
  } = useEmoji();
  const {
    applyOptimisticDropUpdate
  } = useMyStream();
  const rollbackRef = useRef(null);
  let t1;
  if ($[0] !== onOpenDetailDialog || $[1] !== reaction.reaction) {
    t1 = () => {
      onOpenDetailDialog(reaction.reaction);
    };
    $[0] = onOpenDetailDialog;
    $[1] = reaction.reaction;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleLongPressStart = t1;
  let t2;
  if ($[3] !== handleLongPressStart || $[4] !== isTouchDevice) {
    t2 = {
      hasTouchScreen: isTouchDevice,
      onInteractionStart: handleLongPressStart,
      longPressDuration: 400,
      preventDefault: false
    };
    $[3] = handleLongPressStart;
    $[4] = isTouchDevice;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    longPressTriggered,
    touchHandlers
  } = useLongPressInteraction(t2);
  let t3;
  if ($[6] !== touchHandlers) {
    t3 = {
      onTouchStart: e => {
        e.stopPropagation();
        touchHandlers.onTouchStart(e);
      },
      onTouchMove: e_0 => {
        e_0.stopPropagation();
        touchHandlers.onTouchMove(e_0);
      },
      onTouchEnd: e_1 => {
        e_1.stopPropagation();
        touchHandlers.onTouchEnd();
      },
      onTouchCancel: e_2 => {
        e_2.stopPropagation();
        touchHandlers.onTouchCancel();
      }
    };
    $[6] = touchHandlers;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const wrappedTouchHandlers = t3;
  const [total, setTotal] = useState(reaction.profiles.length);
  const [selected, setSelected] = useState(reaction.reaction === drop.context_profile_context?.reaction);
  const [animate, setAnimate] = useState(false);
  const prevTotalRef = useRef(total);
  const prevContextReactionRef = useRef(drop.context_profile_context?.reaction);
  const prevProfilesRef = useRef(reaction.profiles);
  let t4;
  if ($[8] !== drop.context_profile_context?.reaction || $[9] !== reaction.reaction) {
    t4 = () => {
      if (drop.context_profile_context?.reaction === prevContextReactionRef.current) {
        return;
      }
      prevContextReactionRef.current = drop.context_profile_context?.reaction;
      const isSelected = reaction.reaction === drop.context_profile_context?.reaction;
      const timeoutId = setTimeout(() => {
        setSelected(current => current === isSelected ? current : isSelected);
      }, 0);
      return () => clearTimeout(timeoutId);
    };
    $[8] = drop.context_profile_context?.reaction;
    $[9] = reaction.reaction;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = drop.context_profile_context?.reaction;
  let t6;
  if ($[11] !== reaction.reaction || $[12] !== t5) {
    t6 = [t5, reaction.reaction];
    $[11] = reaction.reaction;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  useEffect(t4, t6);
  let t7;
  let t8;
  if ($[14] !== reaction.profiles) {
    t7 = () => {
      if (reaction.profiles === prevProfilesRef.current) {
        return;
      }
      prevProfilesRef.current = reaction.profiles;
      const nextTotal = reaction.profiles.length;
      const timeoutId_0 = setTimeout(() => {
        setTotal(current_0 => current_0 === nextTotal ? current_0 : nextTotal);
      }, 0);
      return () => clearTimeout(timeoutId_0);
    };
    t8 = [reaction.profiles];
    $[14] = reaction.profiles;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[17] !== total) {
    t9 = () => {
      if (total === prevTotalRef.current) {
        return;
      }
      prevTotalRef.current = total;
      const timeoutId_1 = setTimeout(() => setAnimate(true), 0);
      return () => clearTimeout(timeoutId_1);
    };
    t10 = [total];
    $[17] = total;
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[20] !== animate) {
    t11 = () => {
      if (!animate) {
        return;
      }
      const timeout = setTimeout(() => setAnimate(false), 100);
      return () => clearTimeout(timeout);
    };
    t12 = [animate];
    $[20] = animate;
    $[21] = t11;
    $[22] = t12;
  } else {
    t11 = $[21];
    t12 = $[22];
  }
  useEffect(t11, t12);
  let t13;
  if ($[23] !== reaction.reaction) {
    t13 = reaction.reaction.replaceAll(":", "");
    $[23] = reaction.reaction;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const emojiId = t13;
  let t14;
  if ($[25] !== drop.id || $[26] !== emojiId) {
    t14 = buildTooltipId("reaction", drop.id, emojiId);
    $[25] = drop.id;
    $[26] = emojiId;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const tooltipId = t14;
  const waveId = drop.wave.id;
  let t15;
  bb0: {
    let t16;
    if ($[28] !== emojiId || $[29] !== emojiMap) {
      let t17;
      if ($[31] !== emojiId) {
        t17 = e_3 => e_3.id === emojiId;
        $[31] = emojiId;
        $[32] = t17;
      } else {
        t17 = $[32];
      }
      t16 = emojiMap.flatMap(_temp).find(t17);
      $[28] = emojiId;
      $[29] = emojiMap;
      $[30] = t16;
    } else {
      t16 = $[30];
    }
    const custom = t16;
    const customSrc = custom?.skins[0]?.src;
    if (customSrc) {
      let t17;
      if ($[33] !== customSrc || $[34] !== emojiId) {
        t17 = <div className="tw-relative tw-size-4"><Image src={customSrc} alt={emojiId} fill={true} className="tw-object-contain" /></div>;
        $[33] = customSrc;
        $[34] = emojiId;
        $[35] = t17;
      } else {
        t17 = $[35];
      }
      let t18;
      if ($[36] !== customSrc || $[37] !== emojiId) {
        t18 = <div className="tw-relative tw-size-8"><Image src={customSrc} alt={emojiId} fill={true} className="tw-rounded-sm tw-object-contain" /></div>;
        $[36] = customSrc;
        $[37] = emojiId;
        $[38] = t18;
      } else {
        t18 = $[38];
      }
      let t19;
      if ($[39] !== t17 || $[40] !== t18) {
        t19 = {
          emojiNode: t17,
          emojiNodeTooltip: t18
        };
        $[39] = t17;
        $[40] = t18;
        $[41] = t19;
      } else {
        t19 = $[41];
      }
      t15 = t19;
      break bb0;
    }
    let t17;
    if ($[42] !== emojiId || $[43] !== findNativeEmoji) {
      t17 = findNativeEmoji(emojiId);
      $[42] = emojiId;
      $[43] = findNativeEmoji;
      $[44] = t17;
    } else {
      t17 = $[44];
    }
    const native = t17;
    if (native) {
      const t18 = native.skins[0]?.native;
      let t19;
      if ($[45] !== t18) {
        t19 = <span className="tw-flex tw-items-center tw-justify-center tw-text-[1rem]">{t18}</span>;
        $[45] = t18;
        $[46] = t19;
      } else {
        t19 = $[46];
      }
      const t20 = native.skins[0]?.native;
      let t21;
      if ($[47] !== t20) {
        t21 = <span className="tw-flex tw-items-center tw-justify-center tw-text-2xl">{t20}</span>;
        $[47] = t20;
        $[48] = t21;
      } else {
        t21 = $[48];
      }
      let t22;
      if ($[49] !== t19 || $[50] !== t21) {
        t22 = {
          emojiNode: t19,
          emojiNodeTooltip: t21
        };
        $[49] = t19;
        $[50] = t21;
        $[51] = t22;
      } else {
        t22 = $[51];
      }
      t15 = t22;
      break bb0;
    }
    let t18;
    if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = {
        emojiNode: null,
        emojiNodeTooltip: null
      };
      $[52] = t18;
    } else {
      t18 = $[52];
    }
    t15 = t18;
  }
  const {
    emojiNode,
    emojiNodeTooltip
  } = t15;
  let t16;
  if ($[53] !== applyOptimisticDropUpdate || $[54] !== connectedProfile || $[55] !== drop.context_profile_context || $[56] !== drop.id || $[57] !== reaction.reaction || $[58] !== waveId) {
    t16 = willSelect => {
      if (!waveId) {
        return;
      }
      const userProfileMin = toProfileMin(connectedProfile);
      rollbackRef.current = applyOptimisticDropUpdate({
        waveId,
        dropId: drop.id,
        update: draft => {
          if (draft.type !== DropSize.FULL) {
            return draft;
          }
          const reactions = cloneReactionEntries(draft.reactions);
          const userId = connectedProfile?.id ?? null;
          const reactionsWithoutUser = removeUserFromReactions(reactions, userId);
          if (willSelect && userProfileMin) {
            const existingIndex = findReactionIndex(reactionsWithoutUser, reaction.reaction);
            if (existingIndex >= 0) {
              const target = reactionsWithoutUser[existingIndex];
              reactionsWithoutUser[existingIndex] = {
                ...target,
                profiles: [...target.profiles, userProfileMin]
              };
            } else {
              reactionsWithoutUser.push({
                reaction: reaction.reaction,
                profiles: [userProfileMin]
              });
            }
          }
          draft.reactions = reactionsWithoutUser;
          const existingContext = draft.context_profile_context ?? drop.context_profile_context ?? {
            rating: 0,
            min_rating: 0,
            max_rating: 0,
            reaction: null,
            boosted: false,
            bookmarked: false,
            curatable: false,
            curated: false
          };
          draft.context_profile_context = {
            ...existingContext,
            reaction: willSelect ? reaction.reaction : null
          };
          return draft;
        }
      })?.rollback ?? null;
    };
    $[53] = applyOptimisticDropUpdate;
    $[54] = connectedProfile;
    $[55] = drop.context_profile_context;
    $[56] = drop.id;
    $[57] = reaction.reaction;
    $[58] = waveId;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  const applyOptimisticReactionChange = t16;
  let t17;
  if ($[60] !== applyOptimisticReactionChange || $[61] !== drop.id || $[62] !== longPressTriggered || $[63] !== reaction.reaction || $[64] !== selected || $[65] !== setToast) {
    t17 = async () => {
      if (longPressTriggered) {
        return;
      }
      setSelected(_temp2);
      setTotal(n => Math.max(0, n + (selected ? -1 : 1)));
      applyOptimisticReactionChange(!selected);
      if (!selected) {
        recordReaction(reaction.reaction);
      }
      ;
      try {
        const body = {
          reaction: reaction.reaction
        };
        const endpoint = `drops/${drop.id}/reaction`;
        if (selected) {
          await commonApiDelete({
            endpoint
          });
        } else {
          await commonApiPost({
            endpoint,
            body
          });
        }
      } catch (t18) {
        const error = t18;
        let msg = selected ? "Error removing reaction" : "Error adding reaction";
        if (typeof error === "string") {
          msg = error;
        }
        setToast({
          message: msg,
          type: "error"
        });
        setSelected(_temp3);
        setTotal(n_0 => Math.max(0, n_0 + (selected ? 1 : -1)));
        rollbackRef.current?.();
        rollbackRef.current = null;
      }
      rollbackRef.current = null;
    };
    $[60] = applyOptimisticReactionChange;
    $[61] = drop.id;
    $[62] = longPressTriggered;
    $[63] = reaction.reaction;
    $[64] = selected;
    $[65] = setToast;
    $[66] = t17;
  } else {
    t17 = $[66];
  }
  const handleClick = t17;
  let t18;
  if ($[67] !== reaction.profiles) {
    t18 = reaction.profiles.slice(0, 3);
    $[67] = reaction.profiles;
    $[68] = t18;
  } else {
    t18 = $[68];
  }
  const displayProfiles = t18;
  const moreCount = total > 3 ? total - 3 : 0;
  let t19;
  if ($[69] !== displayProfiles || $[70] !== moreCount) {
    t19 = {
      displayProfiles,
      moreCount
    };
    $[69] = displayProfiles;
    $[70] = moreCount;
    $[71] = t19;
  } else {
    t19 = $[71];
  }
  const tooltipProfiles = t19;
  let t20;
  if ($[72] !== onOpenDetailDialog || $[73] !== reaction.reaction) {
    t20 = e_4 => {
      e_4.stopPropagation();
      onOpenDetailDialog(reaction.reaction);
    };
    $[72] = onOpenDetailDialog;
    $[73] = reaction.reaction;
    $[74] = t20;
  } else {
    t20 = $[74];
  }
  const handleMoreClick = t20;
  const borderStyle = selected ? "tw-border-primary-500" : "tw-border-iron-700";
  const bgStyle = selected ? "tw-bg-primary-500/10" : "tw-bg-iron-900/40";
  const hoverStyle = selected ? "hover:tw-border-primary-500 hover:tw-bg-primary-500/10" : "hover:tw-border-iron-500 hover:tw-bg-iron-900/40";
  let animationStyle = "";
  if (animate) {
    if (selected) {
      animationStyle = styles.reactionSlideUp;
    } else {
      animationStyle = styles.reactionSlideDown;
    }
  }
  if (!emojiNode || total === 0) {
    return null;
  }
  let t21;
  if ($[75] !== isTouchDevice || $[76] !== tooltipId) {
    t21 = !isTouchDevice && {
      "data-tooltip-id": tooltipId
    };
    $[75] = isTouchDevice;
    $[76] = tooltipId;
    $[77] = t21;
  } else {
    t21 = $[77];
  }
  let t22;
  if ($[78] !== bgStyle || $[79] !== borderStyle || $[80] !== hoverStyle) {
    t22 = clsx("tw-mt-1 tw-inline-flex tw-items-center tw-gap-x-2 tw-rounded-lg tw-border tw-border-solid tw-px-2 tw-py-1 tw-shadow-sm hover:tw-text-iron-100", borderStyle, bgStyle, hoverStyle);
    $[78] = bgStyle;
    $[79] = borderStyle;
    $[80] = hoverStyle;
    $[81] = t22;
  } else {
    t22 = $[81];
  }
  let t23;
  if ($[82] !== emojiNode) {
    t23 = <div className="tw-flex tw-size-5 tw-flex-shrink-0 tw-items-center tw-justify-center">{emojiNode}</div>;
    $[82] = emojiNode;
    $[83] = t23;
  } else {
    t23 = $[83];
  }
  let t24;
  if ($[84] !== animationStyle) {
    t24 = clsx("tw-min-w-[2ch] tw-text-xs tw-font-normal", animationStyle);
    $[84] = animationStyle;
    $[85] = t24;
  } else {
    t24 = $[85];
  }
  let t25;
  if ($[86] !== total) {
    t25 = formatLargeNumber(total);
    $[86] = total;
    $[87] = t25;
  } else {
    t25 = $[87];
  }
  let t26;
  if ($[88] !== t24 || $[89] !== t25) {
    t26 = <span className={t24}>{t25}</span>;
    $[88] = t24;
    $[89] = t25;
    $[90] = t26;
  } else {
    t26 = $[90];
  }
  let t27;
  if ($[91] !== t23 || $[92] !== t26) {
    t27 = <div className="tw-flex tw-h-full tw-items-center tw-gap-x-1">{t23}{t26}</div>;
    $[91] = t23;
    $[92] = t26;
    $[93] = t27;
  } else {
    t27 = $[93];
  }
  let t28;
  if ($[94] !== handleClick || $[95] !== t21 || $[96] !== t22 || $[97] !== t27 || $[98] !== wrappedTouchHandlers) {
    t28 = <button onClick={handleClick} {...t21} data-text-selection-exclude="true" className={t22} {...wrappedTouchHandlers}>{t27}</button>;
    $[94] = handleClick;
    $[95] = t21;
    $[96] = t22;
    $[97] = t27;
    $[98] = wrappedTouchHandlers;
    $[99] = t28;
  } else {
    t28 = $[99];
  }
  let t29;
  if ($[100] !== emojiNodeTooltip || $[101] !== handleMoreClick || $[102] !== isTouchDevice || $[103] !== tooltipId || $[104] !== tooltipProfiles) {
    t29 = !isTouchDevice && <Tooltip id={tooltipId} delayShow={250} place="bottom" opacity={1} clickable={true} style={{
      backgroundColor: "#37373E",
      color: "white",
      zIndex: 50
    }}><div className="tw-flex tw-items-center tw-gap-2">{emojiNodeTooltip}<span className="tw-whitespace-nowrap">by{" "}{tooltipProfiles.displayProfiles.map((profile, index) => {
            const displayName = profile.handle ?? profile.id;
            const isLast = index === tooltipProfiles.displayProfiles.length - 1;
            const showComma = !isLast;
            return <span key={profile.id}>{profile.handle ? <Link href={`/${profile.handle}`} className="tw-text-primary-400 tw-no-underline hover:tw-text-primary-300 hover:tw-underline" onClick={_temp4}>{displayName}</Link> : <span>{displayName}</span>}{showComma && ", "}</span>;
          })}{tooltipProfiles.moreCount > 0 && <>{" "}<button type="button" onClick={handleMoreClick} className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-primary-400 tw-underline hover:tw-text-primary-300">and {tooltipProfiles.moreCount}{" "}{tooltipProfiles.moreCount === 1 ? "other" : "others"}</button></>}</span></div></Tooltip>;
    $[100] = emojiNodeTooltip;
    $[101] = handleMoreClick;
    $[102] = isTouchDevice;
    $[103] = tooltipId;
    $[104] = tooltipProfiles;
    $[105] = t29;
  } else {
    t29 = $[105];
  }
  let t30;
  if ($[106] !== t28 || $[107] !== t29) {
    t30 = <>{t28}{t29}</>;
    $[106] = t28;
    $[107] = t29;
    $[108] = t30;
  } else {
    t30 = $[108];
  }
  return t30;
}
function _temp4(e_5) {
  return e_5.stopPropagation();
}
function _temp3(s_0) {
  return !s_0;
}
function _temp2(s) {
  return !s;
}
function _temp(cat) {
  return cat.emojis;
}
export default WaveDropReactions;